/**
 * The pieces more than one view draws.
 *
 * Nothing here decides anything: a tile knows how to render an AssetCard and an
 * empty state knows how to say "there is nothing here yet, and here is what to
 * do about it". The views own the policy.
 */

import { h, replace } from "./dom";
import { icon } from "./icons";
import { assetImageUrl, cropUrl } from "./api";
import { store } from "./state";
import { duration, formatDate, isFuzzy, personName } from "./format";
import type { AssetCard } from "./types";

// --- toasts ---------------------------------------------------------------

let toastHost: HTMLElement | null = null;


export function toast(message: string, bad = false): void {
  if (!toastHost) {
    toastHost = h("div", { class: "toasts" });
    document.body.appendChild(toastHost);
  }
  const el = h("div", { class: `toast glass${bad ? " bad" : ""}`, text: message });
  toastHost.appendChild(el);
  setTimeout(() => el.remove(), bad ? 6000 : 3200);
}

// --- states ---------------------------------------------------------------

export interface EmptyOpts {
  icon?: string;
  title: string;
  body: Array<Node | string>;
  actions?: HTMLElement[];
}

/**
 * A library with nothing in it is the normal first minute of using this, not an
 * error. A blank pane reads as a broken app, so every view says what is missing
 * and what would fill it.
 */
export function empty(o: EmptyOpts): HTMLElement {
  return h(
    "div",
    { class: "empty" },
    h("div", { class: "ic" }, icon(o.icon ?? "camera")),
    h("h3", { text: o.title }),
    // Each entry is a paragraph, not a run-on.
    //
    // They were concatenated into one `<p>`, so two sentences in the array
    // arrived as "…scanner border trimmed.Nothing is ever applied…" — visible
    // on every empty state in the client, and the more of them there were the
    // worse it read.
    ...o.body.map((line) =>
      typeof line === "string" ? h("p", { text: line }) : h("p", null, line),
    ),
    o.actions?.length ? h("div", { class: "acts" }, ...o.actions) : null,
  );
}

export function loading(text = "loading"): HTMLElement {
  return h("div", { class: "loading", text: `· · ·  ${text}` });
}

export function failure(err: unknown, retry?: () => void): HTMLElement {
  const why = err instanceof Error ? err.message : String(err);
  return empty({
    icon: "info",
    title: "That did not load",
    body: [why],
    actions: retry ? [h("button", { class: "btn", text: "Try again", onclick: retry })] : [],
  });
}

// --- code -----------------------------------------------------------------

export function code(text: string): HTMLElement {
  return h("code", { text });
}

// --- photographs ----------------------------------------------------------

/** One photograph or film, as it appears in every grid in the client. */
/** A short, stable digest of a value — FNV-1a over its JSON. Not security. */
export function fingerprint(value: unknown): string {
  const text = JSON.stringify(value) ?? "";
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

export function tile(a: AssetCard, onOpen?: (a: AssetCard) => void): HTMLElement {
  // Proxy first. The original is the fallback for anything uploaded before
  // proxies existed, and for a file nothing could decode.
  const src = assetImageUrl(store.libraryId, a);
  const when = formatDate(a.capturedAt, a.capturedPrecision, { short: true });
  const who = a.people.length
    ? a.people.map((p) => personName(p.name)).join(", ")
    : a.fileName;

  const flags: HTMLElement[] = [];
  if (a.storyCount) {
    flags.push(
      h("span", { class: "fl story", title: `${a.storyCount} recorded here` }, icon("quote", "i sm")),
    );
  }
  if (a.capturedAt && isFuzzy(a.capturedPrecision)) {
    flags.push(
      h(
        "span",
        { class: "fl fuzzy", title: `Dated to the ${a.capturedPrecision}, not to the day` },
        icon("clock", "i sm"),
      ),
    );
  }
  if (!a.people.length) {
    flags.push(h("span", { class: "fl blank", title: "Nobody named yet" }, icon("people", "i sm")));
  }

  const el = h(
    "button",
    {
      class: "tile",
      type: "button",
      // What a live update matches on, so a changed photograph replaces its
      // own tile and nothing else.
      "data-key": a.id,
      // Everything the tile's click hands to the lightbox, not only what the
      // tile shows: a kept tile must not open a description from before.
      "data-sig": fingerprint(a),
      title: a.caption ?? a.fileName,
      onclick: () => onOpen?.(a),
    },
    src
      ? h("img", { src, alt: a.caption ?? a.fileName, loading: "lazy", decoding: "async" })
      : h("div", { class: "noimg" }),
    flags.length ? h("div", { class: "flags" }, ...flags) : null,
    a.kind === "video"
      ? h("span", { class: "sc", text: duration(a.durationMs) || "film" })
      : null,
    h("div", { class: "cap", text: when }),
    h("div", { class: "cap" }, h("span", { class: "who", text: who })),
  );
  return el;
}

/** A face, or the initials of a name, or a question mark. Never a broken image. */
export function face(
  name: string | null,
  cropKey: string | null,
  cls = "face",
): HTMLElement {
  const url = cropUrl(cropKey);
  if (url) return h("img", { class: cls, src: url, alt: personName(name) });
  const initials = (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return h("div", { class: `${cls} monogram`, text: initials || "?" });
}

/** A labelled group of controls, used by the grain and tree toolbars. */
export function seg<T extends string>(
  label: string,
  options: Array<[T, string]>,
  current: T,
  onPick: (v: T) => void,
): HTMLElement {
  const wrap = h("div", { class: "segwrap" }, h("span", { class: "lbl", text: label }));
  const box = h("div", { class: "tabs seg" });
  for (const [value, text] of options) {
    box.appendChild(
      h("button", {
        type: "button",
        class: value === current ? "on" : "",
        text,
        onclick: () => value !== current && onPick(value),
      }),
    );
  }
  wrap.appendChild(box);
  return wrap;
}

/** Swap a host's children for a promise's result, with a loading and error state. */
export async function into(
  host: HTMLElement,
  work: () => Promise<Node>,
  what = "loading",
): Promise<void> {
  replace(host, loading(what));
  try {
    replace(host, await work());
  } catch (e) {
    replace(host, failure(e, () => void into(host, work, what)));
  }
}
